import { useState, useRef, useCallback, useEffect } from 'react'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getCurrencySymbol } from '../utils/currencies'
import { useTranslation } from '../i18n/LanguageContext'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const PURPLE = '#7C3AED'
const AMBER  = '#D97706'
const GREEN  = '#16A34A'
const TEAL   = '#0D9488'

const getWeightUnit = (country) => {
  if (!country) return 'kg'
  const c = country.toLowerCase()
  if (c.includes('united states') || c.includes('usa') || c === 'us') return 'lb'
  return 'kg'
}

const CATEGORIES = [
  'Clothing','Sarees','Shoes & Sneakers','Leather Items & Bags',
  'Watches & Accessories','Sunglasses','Jewellery','Gold & Precious','Perfumes',
  'Home Decor','Furniture','Kitchen & Cookware','Bedding & Linen',
  'Electronics & Appliances','Mobile & Gadgets','Computer & Accessories',
  'Food & Catering','Organic Foods','Herbal Products','Grocery Items',
  'Bakery & Sweets','Beverages',
  'Medicines','Supplements & Vitamins','Medical Equipment',
  'Beauty & Skincare','Cosmetics & Manufacturing','Hair Care',
  'Toys & Games','Kids Clothing','Baby Products','Pet Food & Supplies','Pet Accessories',
  'Plants, Trees & Seeds','Flowers & Gift Items',
  'Books','Stationeries','Online Courses','Online Subscriptions & Gift Cards',
  'Sports & Fitness','Arts & Crafts','Construction Materials',
  'Vehicles','Land & Property','Other',
]

const CAT_MAP = {
  'Sarees':'SAR','Clothing':'CLT','Jewellery':'JWL','Gold & Precious':'GLD',
  'Food & Catering':'FOD','Electronics & Appliances':'ELC','Mobile & Gadgets':'MOB',
  'Computer & Accessories':'COM','Beauty & Skincare':'BTY','Cosmetics & Manufacturing':'COS',
  'Hair Care':'HAR','Home Decor':'HOM','Furniture':'FRN','Kitchen & Cookware':'KIT',
  'Shoes & Sneakers':'SHO','Leather Items & Bags':'LBG','Watches & Accessories':'WAT',
  'Sunglasses':'SGL','Perfumes':'PRF','Organic Foods':'ORG','Herbal Products':'HRB',
  'Grocery Items':'GRC','Bakery & Sweets':'BAK','Beverages':'BEV','Medicines':'MED',
  'Supplements & Vitamins':'SUP','Medical Equipment':'MEQ','Toys & Games':'TOY',
  'Kids Clothing':'KDS','Baby Products':'BBY','Pet Food & Supplies':'PET',
  'Pet Accessories':'PAC','Plants, Trees & Seeds':'PLT','Flowers & Gift Items':'FLW',
  'Books':'BOK','Stationeries':'STA','Online Courses':'CRS',
  'Online Subscriptions & Gift Cards':'SUB','Sports & Fitness':'SPT','Arts & Crafts':'ART',
  'Construction Materials':'CON','Vehicles':'VEH','Land & Property':'LND','Other':'OTH',
}

const CAT_GUIDE = {
  'Clothing': { icon:'👕', productType:'Kameez / Jersey / T-Shirt', productName:'Argentina Home Jersey 2026', tip:'Use the full descriptive name. Color goes in Color field — not in name or variants.', variantTip:'Add Size only — S, M, L, XL, XXL.', exampleVariants:'Size: S, M, L, XL, XXL', codeExample:'CLT-AHJ-RED-234' },
  'Sarees': { icon:'🥻', productType:'Saree', productName:'Rajshahi Silk Saree', tip:'Name describes the fabric and style. Color in Color field.', variantTip:'Usually no variants — only add for different lengths.', exampleVariants:'Length: 5.5m, 6m', codeExample:'SAR-RSS-RED-512' },
  'Shoes & Sneakers': { icon:'👟', productType:'Sneakers / Running Shoe', productName:'Adidas Adizero Running Shoe', tip:'Include brand and model. Color in Color field.', variantTip:'Add Size only.', exampleVariants:'Size: 36, 37, 38, 39, 40, 41, 42', codeExample:'SHO-AAR-WHT-891' },
  'Mobile & Gadgets': { icon:'📱', productType:'Smartphone / Earbuds / Smartwatch', productName:'Samsung Galaxy A55 5G', tip:'Include brand, model and generation.', variantTip:'Add Storage or Color.', exampleVariants:'Storage: 128GB, 256GB', codeExample:'MOB-SGA-XXX-901' },
  'DEFAULT': { icon:'📦', productType:'Describe the item type clearly', productName:'Full descriptive product name', tip:'Use a clear specific name. Include brand if relevant.', variantTip:'Add dimensions like Size, Color, Weight — whatever varies.', exampleVariants:'e.g. Size: S, M, L', codeExample:'CAT-XXX-XXX-123' },
}

const SMART_FIELDS = {
  'Sarees': [
    { key:'fabric', label:'Fabric', placeholder:'e.g. Pure silk, Cotton, Georgette' },
    { key:'work_type', label:'Work type', placeholder:'e.g. Embroidery, Print, Zari' },
    { key:'length', label:'Length', placeholder:'e.g. 5.5m' },
    { key:'blouse_included', label:'Blouse included', placeholder:'Yes / No' },
  ],
  'Clothing': [
    { key:'material', label:'Material', placeholder:'e.g. Cotton, Polyester, Linen' },
    { key:'gender', label:'Gender', placeholder:'Male / Female / Unisex / Kids' },
    { key:'fit', label:'Fit', placeholder:'e.g. Regular, Slim, Oversized' },
  ],
  'Jewellery': [
    { key:'material', label:'Material', placeholder:'e.g. Gold plated, Sterling silver' },
    { key:'karat', label:'Karat / Purity', placeholder:'e.g. 18k, 22k, 925 silver' },
    { key:'stone', label:'Stone', placeholder:'e.g. Diamond, Ruby, Zircon, None' },
  ],
  'Beauty & Skincare': [
    { key:'brand', label:'Brand', placeholder:'e.g. Nivea, Garnier, Laneige' },
    { key:'volume', label:'Volume / Weight', placeholder:'e.g. 200ml, 50g' },
    { key:'skin_type', label:'For skin type', placeholder:'e.g. Oily, Dry, All skin types' },
  ],
  'Electronics & Appliances': [
    { key:'brand', label:'Brand', placeholder:'e.g. Samsung, Walton, Philips' },
    { key:'model', label:'Model number', placeholder:'e.g. SM-A556B' },
    { key:'warranty', label:'Warranty', placeholder:'e.g. 1 year official, 6 months' },
    { key:'condition', label:'Condition', placeholder:'New / Used / Refurbished' },
  ],
  'Mobile & Gadgets': [
    { key:'brand', label:'Brand', placeholder:'e.g. Apple, Samsung, Xiaomi' },
    { key:'model', label:'Model', placeholder:'e.g. iPhone 15 Pro, Galaxy A55' },
    { key:'condition', label:'Condition', placeholder:'New / Used / Refurbished' },
    { key:'warranty', label:'Warranty', placeholder:'e.g. 1 year, 6 months' },
  ],
  'Food & Catering': [
    { key:'ingredients', label:'Main ingredients', placeholder:'e.g. Chicken, Basmati rice, Spices' },
    { key:'shelf_life', label:'Shelf life', placeholder:'e.g. 24 hours, 3 days' },
    { key:'halal', label:'Dietary', placeholder:'e.g. Halal certified, Vegetarian' },
  ],
  'Medicines': [
    { key:'generic_name', label:'Generic / Active ingredient', placeholder:'e.g. Paracetamol' },
    { key:'dosage', label:'Dosage / Strength', placeholder:'e.g. 500mg' },
    { key:'manufacturer', label:'Manufacturer', placeholder:'e.g. Square Pharma, Beximco' },
    { key:'expiry_date', label:'Expiry date', placeholder:'e.g. Dec 2027' },
  ],
  'Supplements & Vitamins': [
    { key:'brand', label:'Brand', placeholder:'e.g. GNC, Myprotein' },
    { key:'serving_size', label:'Serving size', placeholder:'e.g. 30g per scoop' },
    { key:'servings', label:'Servings per pack', placeholder:'e.g. 30 servings' },
  ],
}

const VARIANT_SUGGESTIONS = {
  'Clothing':               [{ name:'Size', values:'S, M, L, XL, XXL, 3XL' }],
  'Kids Clothing':          [{ name:'Age', values:'2Y, 3Y, 4Y, 5Y, 6Y, 8Y, 10Y, 12Y' }],
  'Shoes & Sneakers':       [{ name:'Size', values:'35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45' }],
  'Supplements & Vitamins': [{ name:'Flavour', values:'Chocolate, Vanilla, Strawberry' }, { name:'Pack', values:'1kg, 2.5kg, 5kg' }],
  'Beauty & Skincare':      [{ name:'Size', values:'30ml, 50ml, 100ml' }],
  'Food & Catering':        [{ name:'Portion', values:'Single, Family (4), Party (10)' }],
  'Organic Foods':          [{ name:'Weight', values:'250g, 500g, 1kg' }],
  'Beverages':              [{ name:'Size', values:'250ml, 500ml, 1L, 2L' }],
  'Mobile & Gadgets':       [{ name:'Storage', values:'64GB, 128GB, 256GB' }],
  'Medicines':              [{ name:'Pack', values:'Strip of 10, Box of 100' }],
  'Sports & Fitness':       [{ name:'Size', values:'XS, S, M, L, XL' }],
}

const formatWeight = (val, unit) => {
  const n = parseFloat(val)
  if (isNaN(n) || n === 0) return ''
  if (unit === 'lb') return n + ' lb'
  if (n < 1) return Math.round(n * 1000) + 'g'
  return n + ' kg'
}

const inp = {
  width:'100%', border:'1px solid '+BORDER, borderRadius:'9px',
  padding:'9px 12px', fontSize:'12.5px', outline:'none',
  background:WHITE, color:TEXT, fontFamily:'Space Grotesk, sans-serif',
  boxSizing:'border-box', transition:'border-color 0.15s',
}

function FieldInput({ label, value, onChange, placeholder, type='text', optional, hint }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'5px' }}>
        {label}{optional && <span style={{ fontWeight:400, color:'#B0B3B8', marginLeft:'4px' }}>optional</span>}
      </label>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{ ...inp, borderColor:focused?BLUE:BORDER }}
        onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)} />
      {hint && <div style={{ fontSize:'11px', color:MUTED, marginTop:'4px' }}>{hint}</div>}
    </div>
  )
}

function FieldTextarea({ label, value, onChange, placeholder, optional, rows=2 }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'5px' }}>
        {label}{optional && <span style={{ fontWeight:400, color:'#B0B3B8', marginLeft:'4px' }}>optional</span>}
      </label>
      <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows}
        style={{ ...inp, resize:'none', borderColor:focused?BLUE:BORDER }}
        onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)} />
    </div>
  )
}

function SectionCard({ title, color=BLUE, children, hint }) {
  return (
    <div style={{ background:WHITE, borderRadius:'12px', border:'1px solid '+BORDER, overflow:'visible', marginBottom:'14px' }}>
      <div style={{ padding:'10px 16px', borderBottom:'1px solid '+BORDER, background:color+'08', borderRadius:'12px 12px 0 0' }}>
        <div style={{ fontSize:'11px', fontWeight:700, color:color, textTransform:'uppercase', letterSpacing:'0.07em' }}>{title}</div>
        {hint && <div style={{ fontSize:'11px', color:MUTED, marginTop:'2px' }}>{hint}</div>}
      </div>
      <div style={{ padding:'16px' }}>{children}</div>
    </div>
  )
}

function CategoryGuide({ category }) {
  const [open, setOpen] = useState(true)
  const guide = CAT_GUIDE[category] || CAT_GUIDE['DEFAULT']
  return (
    <div style={{ borderRadius:'10px', border:'1px solid '+BLUE+'30', overflow:'hidden', marginBottom:'14px' }}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'#E7F3FF', border:'none', cursor:'pointer' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <span style={{ fontSize:'16px' }}>{guide.icon}</span>
          <span style={{ fontSize:'12.5px', fontWeight:700, color:BLUE }}>How to add a {category} product</span>
        </div>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round"
          style={{ transform:open?'rotate(180deg)':'rotate(0)', transition:'transform 0.15s', flexShrink:0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div style={{ padding:'14px', background:WHITE, display:'flex', flexDirection:'column', gap:'10px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            <div style={{ padding:'10px 12px', borderRadius:'9px', background:BG }}>
              <div style={{ fontSize:'10.5px', fontWeight:700, color:MUTED, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'5px' }}>Product type example</div>
              <div style={{ fontSize:'12.5px', color:TEXT, fontWeight:500 }}>{guide.productType}</div>
            </div>
            <div style={{ padding:'10px 12px', borderRadius:'9px', background:BG }}>
              <div style={{ fontSize:'10.5px', fontWeight:700, color:MUTED, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'5px' }}>Product name example</div>
              <div style={{ fontSize:'12.5px', color:TEXT, fontWeight:500 }}>{guide.productName}</div>
            </div>
          </div>
          <div style={{ padding:'10px 12px', borderRadius:'9px', background:'#FFF8E6', border:'1px solid #FDE68A' }}>
            <div style={{ fontSize:'11.5px', color:'#92400E', lineHeight:1.7 }}><strong>Name tip:</strong> {guide.tip}</div>
          </div>
          <div style={{ padding:'10px 12px', borderRadius:'9px', background:'#F5F3FF', border:'1px solid #DDD6FE' }}>
            <div style={{ fontSize:'10.5px', fontWeight:700, color:PURPLE, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'4px' }}>Variants</div>
            <div style={{ fontSize:'11.5px', color:'#4C1D95', lineHeight:1.7 }}>{guide.variantTip}<br/><strong>Example:</strong> {guide.exampleVariants}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 12px', borderRadius:'9px', background:'#F0FDF4', border:'1px solid #BBF7D0' }}>
            <div style={{ fontSize:'10.5px', fontWeight:700, color:'#16A34A', textTransform:'uppercase', letterSpacing:'0.05em' }}>Auto code example</div>
            <div style={{ fontFamily:'JetBrains Mono, monospace', fontSize:'13px', fontWeight:700, color:BLUE }}>{guide.codeExample}</div>
          </div>
        </div>
      )}
    </div>
  )
}

function CropModal({ src, onSave, onCancel }) {
  const canvasRef  = useRef()
  const imgRef     = useRef()
  const [scale, setScale]       = useState(1)
  const [offsetX, setOffsetX]   = useState(0)
  const [offsetY, setOffsetY]   = useState(0)
  const [dragging, setDragging] = useState(false)
  const [startPos, setStartPos] = useState({ x:0, y:0 })
  const [startOffset, setStartOffset] = useState({ x:0, y:0 })
  const [imgReady, setImgReady] = useState(false)
  const SIZE = 280
  const draw = useCallback(() => {
    const canvas = canvasRef.current; const img = imgRef.current
    if (!canvas || !img || !imgReady) return
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0,0,SIZE,SIZE)
    const sw = img.naturalWidth*scale, sh = img.naturalHeight*scale
    const dx = (SIZE-sw)/2+offsetX, dy = (SIZE-sh)/2+offsetY
    ctx.drawImage(img,dx,dy,sw,sh)
    ctx.strokeStyle = 'rgba(24,119,242,0.2)'; ctx.lineWidth = 0.5; ctx.beginPath()
    ctx.moveTo(SIZE/3,0);ctx.lineTo(SIZE/3,SIZE);ctx.moveTo(SIZE*2/3,0);ctx.lineTo(SIZE*2/3,SIZE)
    ctx.moveTo(0,SIZE/3);ctx.lineTo(SIZE,SIZE/3);ctx.moveTo(0,SIZE*2/3);ctx.lineTo(SIZE,SIZE*2/3)
    ctx.stroke()
  }, [scale,offsetX,offsetY,imgReady])
  useEffect(()=>{ draw() },[draw])
  const handleSave = () => {
    const OUT=800, out=document.createElement('canvas')
    out.width=out.height=OUT
    const ctx=out.getContext('2d'), img=imgRef.current, ratio=OUT/SIZE
    ctx.fillStyle='#FFFFFF'; ctx.fillRect(0,0,OUT,OUT)
    const sw=img.naturalWidth*scale*ratio, sh=img.naturalHeight*scale*ratio
    const dx=(OUT-sw)/2+offsetX*ratio, dy=(OUT-sh)/2+offsetY*ratio
    ctx.drawImage(img,dx,dy,sw,sh)
    out.toBlob(blob=>{ if(blob) onSave(blob) },'image/jpeg',0.92)
  }
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 }}>
      <div style={{ background:WHITE, borderRadius:'16px', padding:'24px', width:'340px', fontFamily:'Space Grotesk, sans-serif', maxHeight:'96vh', overflow:'auto' }}>
        <div style={{ fontSize:'14px', fontWeight:700, color:TEXT, marginBottom:'4px' }}>Crop photo</div>
        <div style={{ fontSize:'11.5px', color:MUTED, marginBottom:'12px' }}>Drag to reposition · Zoom to fit</div>
        <img ref={imgRef} src={src} alt="crop"
          onLoad={()=>{ setImgReady(true); const img=imgRef.current; if(img){ const fit=Math.max(SIZE/img.naturalWidth,SIZE/img.naturalHeight); setScale(fit);setOffsetX(0);setOffsetY(0) } }}
          style={{ display:'none' }} />
        <canvas ref={canvasRef} width={SIZE} height={SIZE}
          style={{ borderRadius:'12px', border:'2px solid '+BORDER, cursor:dragging?'grabbing':'grab', display:'block', margin:'0 auto 12px', width:'100%', touchAction:'none' }}
          onMouseDown={e=>{ setDragging(true);setStartPos({x:e.clientX,y:e.clientY});setStartOffset({x:offsetX,y:offsetY}) }}
          onMouseMove={e=>{ if(!dragging)return;setOffsetX(startOffset.x+e.clientX-startPos.x);setOffsetY(startOffset.y+e.clientY-startPos.y) }}
          onMouseUp={()=>setDragging(false)} onMouseLeave={()=>setDragging(false)} />
        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
          <span style={{ fontSize:'11.5px', color:MUTED, flexShrink:0 }}>Zoom</span>
          <input type="range" min={0.1} max={4} step={0.05} value={scale} onChange={e=>setScale(Number(e.target.value))} style={{ flex:1, accentColor:BLUE }} />
          <span style={{ fontSize:'11.5px', color:MUTED, flexShrink:0 }}>{Math.round(scale*100)}%</span>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'10px', borderRadius:'9px', border:'1px solid '+BORDER, background:WHITE, fontSize:'13px', fontWeight:600, color:MUTED, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} style={{ flex:1, padding:'10px', borderRadius:'9px', background:BLUE, border:'none', fontSize:'13px', fontWeight:600, color:WHITE, cursor:'pointer' }}>Save crop</button>
        </div>
      </div>
    </div>
  )
}

function MatrixBuilder({ variants, setVariants, generateVariantCode, productCode, defaultPrice, suggestedDimensions, weightUnit }) {
  const [dimensions, setDimensions] = useState(
    suggestedDimensions?.length > 0 ? suggestedDimensions.map(d => ({ name: d.name, values: d.values })) : [{ name: '', values: '' }]
  )
  const [generated, setGenerated] = useState(false)
  const [parsedDims, setParsedDims] = useState([])
  const [bulkStock, setBulkStock] = useState('')
  const [bulkPrice, setBulkPrice] = useState('')
  const [bulkWeight, setBulkWeight] = useState('')
  const [viewMode, setViewMode] = useState('list')

  useEffect(() => {
    if (variants.length > 0 && productCode) {
      setVariants(vs => vs.map(v => ({ ...v, variant_code: generateVariantCode(productCode, v.variant_name) })))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCode])

  const addDimension    = () => setDimensions(d => [...d, { name: '', values: '' }])
  const removeDimension = (i) => setDimensions(d => d.filter((_, idx) => idx !== i))
  const updateDimension = (i, field, val) => setDimensions(d => d.map((dim, idx) => idx === i ? { ...dim, [field]: val } : dim))
  const validDimensions = dimensions.filter(d => d.name.trim() && d.values.trim())
  const totalCombos = () => {
    if (!validDimensions.length) return 0
    return validDimensions.reduce((acc, d) => acc * d.values.split(/[,\n]/).filter(v => v.trim()).length, 1)
  }
  const handleGenerate = () => {
    if (!validDimensions.length) return
    const parsed = validDimensions.map(d => ({ name: d.name.trim(), vals: d.values.split(/[,\n]/).map(v => v.trim()).filter(Boolean) }))
    setParsedDims(parsed)
    const cartesian = (arr) => arr.reduce((acc, vals) => acc.flatMap(combo => vals.map(v => [...combo, v])), [[]])
    const combos = cartesian(parsed.map(d => d.vals))
    setVariants(combos.map(combo => ({ variant_name: combo.join(' / '), stock: '', sell_price: '', weight: '', variant_code: generateVariantCode(productCode, combo.join(' / ')) })))
    setGenerated(true)
    setViewMode(parsed.length === 2 ? 'grid' : 'list')
  }
  const applyBulk = () => {
    setVariants(vs => vs.map(v => ({ ...v, stock: bulkStock !== '' ? bulkStock : v.stock, sell_price: bulkPrice !== '' ? bulkPrice : v.sell_price, weight: bulkWeight !== '' ? bulkWeight : v.weight })))
  }
  const updateVariant = (i, field, value) => { setVariants(vs => vs.map((vv, ii) => ii === i ? { ...vv, [field]: value } : vv)) }
  const getVariantByCombo = (rowVal, colVal) => { const name = rowVal + ' / ' + colVal; return variants.find(v => v.variant_name === name) }
  const getVariantIndexByCombo = (rowVal, colVal) => { const name = rowVal + ' / ' + colVal; return variants.findIndex(v => v.variant_name === name) }
  const dim1Vals = parsedDims[0]?.vals || []
  const dim2Vals = parsedDims[1]?.vals || []

  return (
    <div style={{ border:'1px solid #DDD6FE', borderRadius:'12px', background:'#FAFAFF', padding:'14px' }}>
      <div style={{ fontSize:'12px', fontWeight:700, color:PURPLE, marginBottom:'6px' }}>Matrix builder — auto-generate all combinations</div>
      <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'10px' }}>
        {dimensions.map((dim, i) => (
          <div key={i} style={{ display:'grid', gridTemplateColumns:'160px 1fr auto', gap:'8px', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontSize:'10.5px', color:MUTED, marginBottom:'3px' }}>{i===0?'Dimension 1':i===1?'Dimension 2':`Dimension ${i+1}`}</div>
              <input value={dim.name} onChange={e=>updateDimension(i,'name',e.target.value)} placeholder="e.g. Size, Color" style={{ ...inp, fontSize:'12px', borderColor:BORDER }}/>
            </div>
            <div>
              <div style={{ fontSize:'10.5px', color:MUTED, marginBottom:'3px' }}>Values — comma separated</div>
              <input value={dim.values} onChange={e=>updateDimension(i,'values',e.target.value)} placeholder="e.g. S, M, L, XL" style={{ ...inp, fontSize:'12px', borderColor:BORDER }}/>
            </div>
            <div style={{ paddingTop:'18px' }}>
              {dimensions.length > 1 && (
                <button onClick={()=>removeDimension(i)} style={{ width:'28px', height:'28px', borderRadius:'7px', background:'#FEF2F2', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'12px', flexWrap:'wrap' }}>
        <button onClick={addDimension} style={{ padding:'7px 14px', borderRadius:'8px', border:'1px solid #DDD6FE', background:WHITE, fontSize:'12px', fontWeight:600, color:PURPLE, cursor:'pointer' }}>+ Add dimension</button>
        {totalCombos()>0&&<span style={{ fontSize:'11.5px', color:MUTED }}>Will generate <strong style={{ color:PURPLE }}>{totalCombos()} combinations</strong></span>}
        <button onClick={handleGenerate} disabled={totalCombos()===0} style={{ padding:'7px 18px', borderRadius:'8px', background:totalCombos()>0?PURPLE:BORDER, border:'none', fontSize:'12px', fontWeight:700, color:WHITE, cursor:totalCombos()>0?'pointer':'not-allowed', marginLeft:'auto' }}>Generate</button>
      </div>
      {generated && variants.length > 0 && (
        <div>
          <div style={{ height:'1px', background:'#DDD6FE', margin:'12px 0' }}/>
          <div style={{ padding:'10px 12px', borderRadius:'9px', background:'#F5F3FF', border:'1px solid #DDD6FE', marginBottom:'12px' }}>
            <div style={{ fontSize:'11px', fontWeight:700, color:PURPLE, marginBottom:'8px' }}>Bulk fill</div>
            <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
              <input type="number" value={bulkStock} onChange={e=>setBulkStock(e.target.value)} placeholder="Stock for all" style={{ ...inp, width:'110px', fontSize:'12px', borderColor:BORDER }}/>
              <input type="number" value={bulkPrice} onChange={e=>setBulkPrice(e.target.value)} placeholder={defaultPrice?'Price (default: '+defaultPrice+')':'Price for all'} style={{ ...inp, flex:1, minWidth:'110px', fontSize:'12px', borderColor:BORDER }}/>
              <div style={{ position:'relative', width:'90px' }}>
                <input type="number" step="0.001" value={bulkWeight} onChange={e=>setBulkWeight(e.target.value)} placeholder="Weight" style={{ ...inp, fontSize:'12px', borderColor:BORDER, paddingRight:'28px' }}/>
                <span style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', fontSize:'9px', color:MUTED, fontWeight:700 }}>{weightUnit||'kg'}</span>
              </div>
              <button onClick={applyBulk} style={{ padding:'8px 16px', borderRadius:'8px', background:PURPLE, border:'none', fontSize:'12px', fontWeight:600, color:WHITE, cursor:'pointer', flexShrink:0 }}>Apply to all</button>
            </div>
          </div>
          {parsedDims.length===2&&(
            <div style={{ display:'flex', gap:'4px', marginBottom:'12px', padding:'3px', borderRadius:'8px', background:BG, width:'fit-content' }}>
              {[{k:'grid',l:'⊞ Grid'},{k:'list',l:'≡ List'}].map(v=>(
                <button key={v.k} onClick={()=>setViewMode(v.k)} style={{ padding:'5px 12px', borderRadius:'6px', border:'none', fontSize:'11.5px', fontWeight:600, cursor:'pointer', background:viewMode===v.k?PURPLE:'transparent', color:viewMode===v.k?WHITE:MUTED }}>{v.l}</button>
              ))}
            </div>
          )}
          {viewMode==='grid'&&parsedDims.length===2&&(
            <div style={{ overflowX:'auto', marginBottom:'10px' }}>
              <table style={{ borderCollapse:'collapse', minWidth:'100%' }}>
                <thead>
                  <tr>
                    <td style={{ padding:'6px 10px', background:'#F5F3FF', border:'1px solid #DDD6FE', fontSize:'10.5px', fontWeight:700, color:PURPLE }}>{parsedDims[0].name} / {parsedDims[1].name}</td>
                    {dim2Vals.map(col=><td key={col} style={{ padding:'6px 10px', background:'#F5F3FF', border:'1px solid #DDD6FE', fontSize:'11.5px', fontWeight:700, color:PURPLE, textAlign:'center' }}>{col}</td>)}
                  </tr>
                </thead>
                <tbody>
                  {dim1Vals.map(row=>(
                    <tr key={row}>
                      <td style={{ padding:'6px 10px', background:'#F5F3FF', border:'1px solid #DDD6FE', fontSize:'11.5px', fontWeight:700, color:PURPLE }}>{row}</td>
                      {dim2Vals.map(col=>{ const idx=getVariantIndexByCombo(row,col); const v=getVariantByCombo(row,col); if(!v||idx<0) return <td key={col}/>; return (
                        <td key={col} style={{ padding:'4px', border:'1px solid #DDD6FE', background:v.stock?'#F0FDF4':WHITE, minWidth:'110px' }}>
                          <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
                            <input type="number" value={v.stock} onChange={e=>updateVariant(idx,'stock',e.target.value)} placeholder="Qty" style={{ ...inp, fontSize:'11.5px', padding:'4px 7px', borderColor:v.stock?'#BBF7D0':BORDER, textAlign:'center' }}/>
                            <input type="number" value={v.sell_price} onChange={e=>updateVariant(idx,'sell_price',e.target.value)} placeholder="Price" style={{ ...inp, fontSize:'11px', padding:'4px 7px', borderColor:BORDER, textAlign:'center', color:MUTED }}/>
                          </div>
                        </td>
                      )})}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {viewMode==='list'&&(
            <div style={{ display:'flex', flexDirection:'column', gap:'6px', maxHeight:'400px', overflowY:'auto' }}>
              {variants.map((v,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'9px 10px', borderRadius:'9px', border:'1px solid #DDD6FE', background:WHITE }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'12.5px', fontWeight:600, color:TEXT }}>{v.variant_name}</div>
                    {v.variant_code&&<div style={{ fontSize:'10px', fontFamily:'JetBrains Mono, monospace', color:PURPLE, marginTop:'2px' }}>{v.variant_code}</div>}
                  </div>
                  <input type="number" value={v.stock} onChange={e=>updateVariant(i,'stock',e.target.value)} placeholder="Qty" style={{ ...inp, width:'70px', fontSize:'12px', borderColor:BORDER, padding:'7px 9px' }}/>
                  <input type="number" value={v.sell_price} onChange={e=>updateVariant(i,'sell_price',e.target.value)} placeholder="Price" style={{ ...inp, width:'88px', fontSize:'12px', borderColor:BORDER, padding:'7px 9px' }}/>
                  <div style={{ position:'relative', width:'80px' }}>
                    <input type="number" step="0.001" value={v.weight} onChange={e=>updateVariant(i,'weight',e.target.value)} placeholder="Wt" style={{ ...inp, fontSize:'12px', borderColor:BORDER, padding:'7px 24px 7px 9px' }}/>
                    <span style={{ position:'absolute', right:'6px', top:'50%', transform:'translateY(-50%)', fontSize:'9px', color:MUTED, fontWeight:700 }}>{weightUnit||'kg'}</span>
                  </div>
                  <button onClick={()=>setVariants(vs=>vs.filter((_,ii)=>ii!==i))} style={{ width:'24px', height:'24px', borderRadius:'6px', background:'#FEF2F2', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize:'11.5px', color:MUTED, marginTop:'10px' }}>
            Total stock: <strong style={{ color:TEXT }}>{variants.reduce((s,v)=>s+Number(v.stock||0),0)}</strong> · {variants.length} variants
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main AddProduct ──────────────────────────────────────────────────────
function AddProduct() {
  const navigate      = useNavigate()
  const { seller }    = useAuth()
  const { t }         = useTranslation()
  const currency      = seller?.seller_currency || 'BDT'
  const sym           = getCurrencySymbol(currency)
  const weightUnit    = getWeightUnit(seller?.country)

  const [form, setForm] = useState({
    product_type:'', name:'', category:'', color:'',
    sell_price:'', buy_price:'', vat_percent:'', stock:'',
    weight:'', status:'active', season:'all_year', is_pinned:false,
    notes:'', additional_details:'', facebook_post_url:'', external_barcode:'',
  })
  const [smartFields, setSmartFields]       = useState({})
  const [variants, setVariants]             = useState([])
  const [newVariant, setNewVariant]         = useState({ variant_name:'', stock:'', sell_price:'', weight:'' })
  const [variantMode, setVariantMode]       = useState('simple')
  const [saving, setSaving]                 = useState(false)
  const [saved, setSaved]                   = useState(false)
  const [productCode, setProductCode]       = useState('')
  const [codeIsManual, setCodeIsManual]     = useState(false)
  const [codeEditMode, setCodeEditMode]     = useState(false)
  const [duplicateWarning, setDuplicateWarning] = useState(false)
  const [imageFile, setImageFile]           = useState(null)
  const [imagePreview, setImagePreview]     = useState(null)
  const [cropSrc, setCropSrc]               = useState(null)
  const [uploading, setUploading]           = useState(false)

  // Offer
  const [offerActive,     setOfferActive]     = useState(false)
  const [offerType,       setOfferType]       = useState('percent')
  const [offerValue,      setOfferValue]      = useState('')
  const [offerLabel,      setOfferLabel]      = useState('')
  const [offerValidFrom,  setOfferValidFrom]  = useState('')
  const [offerValidUntil, setOfferValidUntil] = useState('')

  // ── NEW: Variable pricing (Tara bargaining) ──
  const [variablePricing, setVariablePricing] = useState(false)
  const [bestPrice,       setBestPrice]       = useState('')

  // ── NEW: Advance payment rule ──
  const [advanceType,  setAdvanceType]  = useState('none')
  const [advanceValue, setAdvanceValue] = useState('')

  // ── NEW: FB post status ──
  const [postedOnFacebook, setPostedOnFacebook] = useState(false)
  const [fbPostedDate,     setFbPostedDate]     = useState('')

  const imgRef           = useRef()
  const barcodeVideoRef  = useRef(null)
  const barcodeStreamRef = useRef(null)
  const barcodeRafRef    = useRef(null)
  const [barcodeCamera, setBarcodeCamera]   = useState(false)
  const [barcodeScanErr, setBarcodeScanErr] = useState('')

  const set = (key, val) => setForm(f=>({...f,[key]:val}))

  const generateCode = useCallback((category, name, color) => {
    const cat      = CAT_MAP[category]||'OTH'
    const nameCode = name?.split(' ').filter(w=>w.length>1).map(w=>w[0]).join('').toUpperCase().slice(0,3)||'XXX'
    const colorCode= color?.trim().slice(0,3).toUpperCase().replace(/[^A-Z]/g,'')||'XXX'
    const seq      = Math.floor(100+Math.random()*900)
    return cat+'-'+nameCode+'-'+colorCode+'-'+seq
  },[])

  const generateVariantCode = useCallback((parentCode, variantName) => {
    if(!parentCode||!variantName) return ''
    const clean = variantName.toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,8)
    return parentCode+'-'+clean
  },[])

  const checkDuplicate = async (code) => {
    if(!code||!seller?.id) return
    const { data } = await supabase.from('products').select('id').eq('seller_id',seller.id).eq('product_code',code).single()
    setDuplicateWarning(!!data)
  }

  const handleCategoryChange = (cat) => {
    set('category',cat); setSmartFields({})
    const newCode = codeIsManual?productCode:generateCode(cat,form.name,form.color)
    if(!codeIsManual) setProductCode(newCode)
    const suggestions = VARIANT_SUGGESTIONS[cat]
    if(suggestions){ setVariants(suggestions.flatMap(dim=>dim.values.split(',').map(v=>({ variant_name:v.trim(), stock:'', sell_price:'', weight:'', variant_code:generateVariantCode(newCode,v.trim()) })))); setVariantMode('simple') }
    else { setVariants([]) }
  }
  const handleNameChange = (val) => {
    set('name',val)
    if(!codeIsManual){ const newCode=generateCode(form.category,val,form.color); setProductCode(newCode); setVariants(vs=>vs.map(v=>({...v,variant_code:generateVariantCode(newCode,v.variant_name)}))) }
  }
  const handleColorChange = (val) => {
    set('color',val)
    if(!codeIsManual){ const newCode=generateCode(form.category,form.name,val); setProductCode(newCode); setVariants(vs=>vs.map(v=>({...v,variant_code:generateVariantCode(newCode,v.variant_name)}))) }
  }
  const handleCodeChange = (val) => {
    const clean = val.toUpperCase().replace(/[^A-Z0-9-]/g,'').slice(0,20)
    setProductCode(clean); setCodeIsManual(true); checkDuplicate(clean)
  }
  const handleRevertToAuto = () => {
    setProductCode(generateCode(form.category,form.name,form.color))
    setCodeIsManual(false); setCodeEditMode(false); setDuplicateWarning(false)
  }
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]; if(!file) return
    const reader = new FileReader(); reader.onload=ev=>setCropSrc(ev.target.result); reader.readAsDataURL(file); e.target.value=''
  }
  const handleCropSave = (blob) => { setImageFile(blob); setImagePreview(URL.createObjectURL(blob)); setCropSrc(null) }
  const uploadImage = async (productId) => {
    if(!imageFile) return null
    try {
      setUploading(true)
      const path = seller.id+'/'+productId+'.jpg'
      const { error } = await supabase.storage.from('product-images').upload(path,imageFile,{upsert:true,contentType:'image/jpeg'})
      setUploading(false); if(error) return null
      const { data } = supabase.storage.from('product-images').getPublicUrl(path)
      return data?.publicUrl||null
    } catch { setUploading(false); return null }
  }

  const stopBarcodeCamera = () => {
    if(barcodeRafRef.current) cancelAnimationFrame(barcodeRafRef.current)
    if(barcodeStreamRef.current) barcodeStreamRef.current.getTracks().forEach(t=>t.stop())
    barcodeStreamRef.current=null; setBarcodeCamera(false); setBarcodeScanErr('')
  }
  const openBarcodeCamera = async () => {
    setBarcodeScanErr(''); setBarcodeCamera(true)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video:{facingMode:'environment',width:{ideal:1280}} })
      barcodeStreamRef.current=stream
      if(barcodeVideoRef.current){ barcodeVideoRef.current.srcObject=stream; barcodeVideoRef.current.play() }
      if('BarcodeDetector' in window){
        const detector = new window.BarcodeDetector({ formats:['ean_13','ean_8','upc_a','upc_e','code_128','code_39','qr_code'] })
        const tick = async()=>{
          if(!barcodeStreamRef.current) return
          try { const barcodes=await detector.detect(barcodeVideoRef.current); if(barcodes.length>0){ set('external_barcode',barcodes[0].rawValue); stopBarcodeCamera(); return } } catch {}
          barcodeRafRef.current=requestAnimationFrame(tick)
        }
        barcodeVideoRef.current.addEventListener('playing',()=>{ barcodeRafRef.current=requestAnimationFrame(tick) },{once:true})
      } else { setBarcodeScanErr('Auto-detect not supported — type the number manually.') }
    } catch { setBarcodeScanErr('Camera access denied.'); setBarcodeCamera(false) }
  }

  const addVariant = () => {
    if(!newVariant.variant_name) return
    setVariants(vs=>[...vs,{...newVariant,variant_code:generateVariantCode(productCode,newVariant.variant_name)}])
    setNewVariant({variant_name:'',stock:'',sell_price:'',weight:''})
  }
  const handleVariantChange = (i,field,value) => setVariants(vs=>vs.map((v,idx)=>idx===i?{...v,[field]:value}:v))

  useEffect(()=>()=>stopBarcodeCamera(),[]) // eslint-disable-line react-hooks/exhaustive-deps

  const totalWeight = () => {
    if(variants.length>0){
      const total = variants.reduce((sum,v)=>{ const qty=Number(v.stock||0); const w=parseFloat(v.weight||form.weight||0); return sum+(qty*w) },0)
      return total>0 ? formatWeight(total,weightUnit) : null
    }
    const qty = Number(form.stock||0); const w = parseFloat(form.weight||0)
    return qty>0&&w>0 ? formatWeight(qty*w,weightUnit) : null
  }

  const handleSave = async () => {
    if(!form.name||!form.category||!productCode||duplicateWarning) return
    setSaving(true)
    const totalStock = variants.length>0 ? variants.reduce((sum,v)=>sum+Number(v.stock||0),0) : Number(form.stock||0)
    const { data:product } = await supabase.from('products').insert({
      seller_id:seller?.id, product_type:form.product_type,
      name:form.name, category:form.category,
      product_code:productCode, code_is_manual:codeIsManual, color:form.color||null,
      sell_price:form.sell_price||null, buy_price:form.buy_price||null,
      vat_percent:form.vat_percent||null, stock:totalStock,
      weight_kg:form.weight?parseFloat(form.weight):null, weight_unit:weightUnit,
      status:form.status, season:form.season, is_pinned:form.is_pinned,
      notes:form.notes, additional_details:form.additional_details,
      facebook_post_url:form.facebook_post_url,
      smart_fields:Object.keys(smartFields).length>0?smartFields:null,
      external_barcode:form.external_barcode||null,
      offer_active:offerActive,
      offer_type:offerActive?offerType:null,
      offer_value:offerActive&&offerValue?Number(offerValue):null,
      offer_label:offerActive&&offerLabel?offerLabel:null,
      offer_valid_from:offerActive&&offerValidFrom?offerValidFrom:null,
      offer_valid_until:offerActive&&offerValidUntil?offerValidUntil:null,
      // ── NEW fields ──
      variable_pricing:variablePricing,
      best_price:variablePricing&&bestPrice?Number(bestPrice):null,
      advance_payment_type:advanceType,
      advance_payment_value:advanceType!=='none'&&advanceType!=='full'&&advanceValue?Number(advanceValue):null,
      posted_on_facebook:postedOnFacebook,
      fb_posted_date:postedOnFacebook&&fbPostedDate?fbPostedDate:null,
    }).select().single()

    if(product){
      const imageUrl = await uploadImage(product.id)
      if(imageUrl) await supabase.from('products').update({image_url:imageUrl}).eq('id',product.id)
      if(variants.length>0){
        await supabase.from('product_variants').insert(
          variants.filter(v=>v.variant_name).map(v=>({
            product_id:product.id, seller_id:seller?.id,
            variant_name:v.variant_name,
            variant_code:v.variant_code||generateVariantCode(productCode,v.variant_name),
            stock:Number(v.stock||0),
            sell_price:v.sell_price||form.sell_price||null,
            weight_kg:v.weight?parseFloat(v.weight):null,
          }))
        )
      }
    }
    setSaving(false); setSaved(true)
  }

  const resetForm = () => {
    setSaved(false)
    setForm({product_type:'',name:'',category:'',color:'',sell_price:'',buy_price:'',vat_percent:'',stock:'',weight:'',status:'active',season:'all_year',is_pinned:false,notes:'',additional_details:'',facebook_post_url:'',external_barcode:''})
    setVariants([]);setSmartFields({});setProductCode('')
    setCodeIsManual(false);setCodeEditMode(false);setDuplicateWarning(false)
    setImageFile(null);setImagePreview(null);setCropSrc(null);setVariantMode('simple')
    setOfferActive(false);setOfferType('percent');setOfferValue('');setOfferLabel('');setOfferValidFrom('');setOfferValidUntil('')
    // ── NEW resets ──
    setVariablePricing(false);setBestPrice('')
    setAdvanceType('none');setAdvanceValue('')
    setPostedOnFacebook(false);setFbPostedDate('')
  }

  if(saved){
    return (
      <div style={{ flex:1, background:BG, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Space Grotesk, sans-serif' }}>
        <div style={{ background:WHITE, borderRadius:'16px', padding:'36px', textAlign:'center', maxWidth:'400px', width:'100%', margin:'16px' }}>
          <div style={{ fontSize:'48px', marginBottom:'12px' }}>✅</div>
          <div style={{ fontSize:'18px', fontWeight:700, color:TEXT, marginBottom:'6px' }}>{t('common.done')} — Product saved!</div>
          <div style={{ fontSize:'13px', color:MUTED, marginBottom:'4px' }}>Code: <span style={{ fontFamily:'JetBrains Mono, monospace', fontWeight:700, color:BLUE }}>{productCode}</span></div>
          {variants.length>0&&<div style={{ fontSize:'12px', color:MUTED, marginBottom:'4px' }}>{variants.length} variants · Stock: {variants.reduce((s,v)=>s+Number(v.stock||0),0)}</div>}
          {variablePricing&&bestPrice&&<div style={{ fontSize:'12px', color:PURPLE, marginBottom:'4px', fontWeight:600 }}>Variable pricing — floor: {sym}{bestPrice}</div>}
          {advanceType!=='none'&&<div style={{ fontSize:'12px', color:TEAL, marginBottom:'4px', fontWeight:600 }}>Advance: {advanceType==='full'?'Full payment required':advanceType==='percent'?advanceValue+'% upfront':sym+advanceValue+' upfront'}</div>}
          {postedOnFacebook&&<div style={{ fontSize:'12px', color:'#1877F2', marginBottom:'4px', fontWeight:600 }}>✓ Posted on Facebook{fbPostedDate?' · '+fbPostedDate:''}</div>}
          <div style={{ fontSize:'12px', color:MUTED, marginBottom:'24px', lineHeight:1.6 }}>Add code <strong style={{ color:BLUE }}>{productCode}</strong> to your Facebook posts so customers can quote it when ordering.</div>
          <div style={{ display:'flex', gap:'10px' }}>
            <button onClick={resetForm} style={{ flex:1, padding:'12px', borderRadius:'10px', background:BLUE, border:'none', fontSize:'13px', fontWeight:700, color:WHITE, cursor:'pointer' }}>Add another</button>
            <button onClick={()=>navigate('/inventory')} style={{ flex:1, padding:'12px', borderRadius:'10px', border:'1px solid '+BORDER, background:WHITE, fontSize:'13px', fontWeight:600, color:TEXT, cursor:'pointer' }}>Inventory</button>
          </div>
        </div>
      </div>
    )
  }

  const smartFieldDefs      = SMART_FIELDS[form.category]||[]
  const profit              = form.sell_price&&form.buy_price ? Number(form.sell_price)-Number(form.buy_price) : null
  const margin              = profit&&form.sell_price ? Math.round((profit/Number(form.sell_price))*100) : null
  const suggestedDimensions = VARIANT_SUGGESTIONS[form.category]||[]
  const stepOffset          = smartFieldDefs.length>0 ? 1 : 0

  return (
    <div style={{ flex:1, background:BG, overflowX:'hidden', fontFamily:'Space Grotesk, sans-serif' }}>
      <Topbar title={t('inventory.add_product')}/>
      <div style={{ padding:'18px 24px', maxWidth:'900px' }}>

        {form.category&&<CategoryGuide category={form.category}/>}

        {/* Step 1 — What are you selling */}
        <SectionCard title="Step 1 — What are you selling?" color={BLUE} hint="Start with category and name. The product code generates automatically.">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }} className="ap-grid">
            <div>
              <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'5px' }}>{t('inventory.category')} *</label>
              <select value={form.category} onChange={e=>handleCategoryChange(e.target.value)} style={{ ...inp, borderColor:BORDER }}>
                <option value="">Select category first</option>
                {CATEGORIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <FieldInput label="Product type *" value={form.product_type} onChange={v=>set('product_type',v)}
              placeholder={form.category?(CAT_GUIDE[form.category]?.productType||'e.g. Jersey, Saree, Laptop'):'Select category first'}
              hint="The specific item type"/>
            <div style={{ gridColumn:'span 2' }}>
              <FieldInput label={t('inventory.product_name')+' *'} value={form.name} onChange={handleNameChange}
                placeholder={form.category?(CAT_GUIDE[form.category]?.productName||'Full descriptive product name'):'Select category first'}
                hint="Be specific. Include brand if relevant. Do NOT include color in the name."/>
            </div>
            <FieldInput label="Color / Visual variation" value={form.color} onChange={handleColorChange}
              placeholder="e.g. Red, Navy Blue, Floral White" optional
              hint="Each color = a separate product with its own photo and code."/>
            <div style={{ padding:'10px 13px', borderRadius:'10px', background:'#E7F3FF', border:'1px solid '+BLUE+'30', fontSize:'11.5px', color:TEXT, lineHeight:1.7, alignSelf:'flex-end' }}>
              <strong style={{ color:BLUE }}>Simple rule:</strong> If it looks different in a photo = separate product. If you cannot see the difference = variant.
            </div>
            <div style={{ gridColumn:'span 2' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'5px' }}>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED }}>
                  {t('inventory.code')}
                  {codeIsManual
                    ? <span style={{ marginLeft:'8px', fontSize:'10px', fontWeight:700, padding:'1px 6px', borderRadius:'4px', background:'#FFF8E6', color:AMBER }}>Custom</span>
                    : productCode&&<span style={{ marginLeft:'8px', fontSize:'10px', fontWeight:700, padding:'1px 6px', borderRadius:'4px', background:'#F0FDF4', color:'#16A34A' }}>Auto-generated</span>}
                </label>
                {!codeEditMode
                  ? <button onClick={()=>setCodeEditMode(true)} style={{ fontSize:'11.5px', color:BLUE, background:'none', border:'none', cursor:'pointer', fontFamily:'Space Grotesk' }}>Edit</button>
                  : <button onClick={handleRevertToAuto} style={{ fontSize:'11.5px', color:MUTED, background:'none', border:'none', cursor:'pointer', fontFamily:'Space Grotesk' }}>Reset to auto</button>}
              </div>
              {!codeEditMode
                ? <div style={{ padding:'9px 12px', borderRadius:'9px', border:'1px solid '+BORDER, background:BG, fontSize:'13px', fontFamily:'JetBrains Mono, monospace', fontWeight:700, color:BLUE }}>{productCode||'Fill name and category above to generate'}</div>
                : <div>
                    <input value={productCode} onChange={e=>handleCodeChange(e.target.value)} maxLength={20} placeholder="e.g. MY-PROD-001" style={{ ...inp, borderColor:duplicateWarning?'#DC2626':BORDER, fontFamily:'JetBrains Mono, monospace' }}/>
                    {duplicateWarning&&<div style={{ fontSize:'11.5px', color:'#DC2626', marginTop:'4px' }}>This code already exists — change it</div>}
                  </div>}
              <div style={{ fontSize:'11px', color:MUTED, marginTop:'4px' }}>Add this code to your Facebook post captions so customers can quote it when ordering.</div>
            </div>
            <div>
              <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'5px' }}>Season / Occasion <span style={{ fontWeight:400, color:'#B0B3B8' }}>optional</span></label>
              <select value={form.season} onChange={e=>set('season',e.target.value)} style={{ ...inp, borderColor:BORDER }}>
                <option value="all_year">All year</option>
                <option value="eid">Eid special</option>
                <option value="winter">Winter</option>
                <option value="summer">Summer</option>
                <option value="wedding">Wedding season</option>
                <option value="puja">Puja</option>
                <option value="fair">Fair / Event</option>
              </select>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', paddingTop:'20px' }}>
              <input type="checkbox" checked={form.is_pinned} onChange={e=>set('is_pinned',e.target.checked)} style={{ width:'16px', height:'16px', accentColor:BLUE, flexShrink:0 }}/>
              <label style={{ fontSize:'12px', color:TEXT, fontWeight:500, cursor:'pointer', lineHeight:1.5 }}>Pin as bestseller — shows first in order search</label>
            </div>
          </div>
        </SectionCard>

        {/* Step 2 — Inventory photo */}
        <SectionCard title="Step 2 — Inventory photo" color={BLUE} hint="Square photo for your inventory and product cards.">
          <div style={{ display:'flex', alignItems:'flex-start', gap:'16px', flexWrap:'wrap' }}>
            {imagePreview
              ? <div style={{ position:'relative', flexShrink:0 }}>
                  <img src={imagePreview} alt="preview" style={{ width:'100px', height:'100px', borderRadius:'12px', objectFit:'cover', border:'2px solid '+BLUE }}/>
                  <button onClick={()=>{ setImageFile(null);setImagePreview(null) }} style={{ position:'absolute', top:'-7px', right:'-7px', width:'22px', height:'22px', borderRadius:'50%', background:'#DC2626', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              : <div style={{ width:'100px', height:'100px', borderRadius:'12px', border:'2px dashed '+BORDER, background:BG, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                </div>}
            <div style={{ flex:1 }}>
              <input ref={imgRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleImageSelect}/>
              <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'8px' }}>
                <button onClick={()=>imgRef.current?.click()} style={{ padding:'9px 18px', borderRadius:'9px', border:'1px solid '+BORDER, background:WHITE, fontSize:'12.5px', fontWeight:600, color:TEXT, cursor:'pointer' }}>
                  {imagePreview?'Change':'Upload'} photo
                </button>
                {imagePreview&&imageFile&&(
                  <button onClick={()=>{ const r=new FileReader();r.onload=e=>setCropSrc(e.target.result);r.readAsDataURL(imageFile) }} style={{ padding:'9px 18px', borderRadius:'9px', border:'1px solid '+BLUE, background:'#E7F3FF', fontSize:'12.5px', fontWeight:600, color:BLUE, cursor:'pointer' }}>Crop</button>
                )}
              </div>
              <div style={{ fontSize:'11.5px', color:MUTED, lineHeight:1.6 }}>JPG, PNG or WebP — will be cropped to square (1:1).</div>
              <div style={{ fontSize:'11px', color:AMBER, marginTop:'3px' }}>Update product photos monthly — fresh photos get more attention</div>
            </div>
          </div>
        </SectionCard>

        {/* Step 3 — Facebook post status (NEW) */}
        <SectionCard title="Step 3 — Facebook post status" color="#1877F2" hint="Mark whether this product has already been posted on your Facebook page.">
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:BG, borderRadius:'10px' }}>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:TEXT }}>Posted on Facebook</div>
                <div style={{ fontSize:'11.5px', color:MUTED, marginTop:'2px' }}>Toggle on once you have shared this product on your page</div>
              </div>
              <button onClick={()=>setPostedOnFacebook(v=>!v)} style={{ width:'40px', height:'22px', borderRadius:'11px', background:postedOnFacebook?'#1877F2':BORDER, border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                <div style={{ position:'absolute', top:'3px', left:postedOnFacebook?'21px':'3px', width:'16px', height:'16px', borderRadius:'50%', background:WHITE, transition:'left 0.2s' }}/>
              </button>
            </div>
            {postedOnFacebook && (
              <FieldInput label="Date posted" value={fbPostedDate} onChange={setFbPostedDate} type="date" optional
                hint="You can also add the full post link from the product detail page after saving." />
            )}
          </div>
        </SectionCard>

        {/* Smart fields */}
        {smartFieldDefs.length>0&&(
          <SectionCard title={`Step ${4+stepOffset} — ${form.category} details`} color={PURPLE} hint="These details help with search and analytics — fill what applies.">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }} className="ap-grid">
              {smartFieldDefs.map(field=>(
                <FieldInput key={field.key} label={field.label} value={smartFields[field.key]||''}
                  onChange={v=>setSmartFields(sf=>({...sf,[field.key]:v}))} placeholder={field.placeholder} optional/>
              ))}
              <div style={{ gridColumn:'span 2' }}>
                <FieldTextarea label="Additional details" value={form.additional_details} onChange={v=>set('additional_details',v)} placeholder="Anything else worth noting..." optional/>
              </div>
            </div>
          </SectionCard>
        )}

        {/* Pricing */}
        <SectionCard title={`Step ${4+stepOffset} — Pricing`} color={AMBER}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px' }} className="ap-grid-3">
            <FieldInput label="Sell price *" value={form.sell_price} onChange={v=>set('sell_price',v)} placeholder="0" type="number"/>
            <div>
              <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'5px' }}>
                Buy / cost price <span style={{ fontSize:'10px', fontWeight:700, padding:'1px 6px', borderRadius:'4px', background:'#FFF8E6', color:AMBER, marginLeft:'6px' }}>Owner only</span>
              </label>
              <input type="number" value={form.buy_price} onChange={e=>set('buy_price',e.target.value)} placeholder="0" style={{ ...inp, borderColor:BORDER }}/>
            </div>
            <FieldInput label="VAT / GST %" value={form.vat_percent} onChange={v=>set('vat_percent',v)} placeholder="e.g. 15" type="number" optional/>
          </div>
          {profit!==null&&profit>=0&&(
            <div style={{ marginTop:'12px', padding:'10px 14px', borderRadius:'10px', background:'#F0FDF4', border:'1px solid #BBF7D0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:'12px', color:'#16A34A' }}>Profit per unit</span>
              <span style={{ fontSize:'14px', fontWeight:700, color:'#16A34A' }}>{sym}{profit.toLocaleString()} <span style={{ fontSize:'12px', fontWeight:400 }}>({margin}% margin)</span></span>
            </div>
          )}
        </SectionCard>

        {/* Variable pricing (NEW) */}
        <SectionCard title={`Step ${4+stepOffset}b — Variable pricing (Tara bargaining)`} color={PURPLE} hint="Let Tara negotiate down to a floor price — useful for products where you accept bargaining.">
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:BG, borderRadius:'10px' }}>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:TEXT }}>Allow Tara to bargain on this product</div>
                <div style={{ fontSize:'11.5px', color:MUTED, marginTop:'2px' }}>Tara will never go below the floor price you set below</div>
              </div>
              <button onClick={()=>setVariablePricing(v=>!v)} style={{ width:'40px', height:'22px', borderRadius:'11px', background:variablePricing?PURPLE:BORDER, border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                <div style={{ position:'absolute', top:'3px', left:variablePricing?'21px':'3px', width:'16px', height:'16px', borderRadius:'50%', background:WHITE, transition:'left 0.2s' }}/>
              </button>
            </div>
            {variablePricing && (
              <FieldInput
                label={'Floor price (' + sym + ') — lowest Tara can agree to'}
                value={bestPrice} onChange={setBestPrice} type="number"
                placeholder={form.sell_price ? 'e.g. ' + Math.round(Number(form.sell_price) * 0.85) : 'e.g. 850'}
                hint={form.sell_price && bestPrice ? 'That is ' + Math.round((1 - Number(bestPrice)/Number(form.sell_price)) * 100) + '% below your listed price of ' + sym + form.sell_price : 'Should be lower than your sell price above'} />
            )}
          </div>
        </SectionCard>

        {/* Advance payment (NEW) */}
        <SectionCard title={`Step ${4+stepOffset}c — Advance payment requirement`} color={TEAL} hint="Tara and the order form will show customers the exact advance amount for this product automatically.">
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            {[
              { v:'none',    l:'No advance needed',    sub:'Customer pays on delivery or as they choose' },
              { v:'percent', l:'Percentage advance',   sub:'e.g. 30% upfront, rest on delivery' },
              { v:'fixed',   l:'Fixed amount advance', sub:'e.g. flat ' + sym + '200 upfront, rest on delivery' },
              { v:'full',    l:'Full payment required',sub:'100% advance — no COD for this product' },
            ].map(opt => (
              <button key={opt.v} onClick={() => setAdvanceType(opt.v)}
                style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'9px', border:'1.5px solid', borderColor: advanceType === opt.v ? TEAL : BORDER, background: advanceType === opt.v ? 'rgba(13,148,136,0.07)' : WHITE, cursor:'pointer', textAlign:'left' }}>
                <div style={{ width:'16px', height:'16px', borderRadius:'50%', border:'2px solid', borderColor: advanceType === opt.v ? TEAL : BORDER, background: advanceType === opt.v ? TEAL : WHITE, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {advanceType === opt.v && <div style={{ width:'6px', height:'6px', borderRadius:'50%', background:WHITE }}/>}
                </div>
                <div>
                  <div style={{ fontSize:'12.5px', fontWeight:700, color: advanceType === opt.v ? TEAL : TEXT }}>{opt.l}</div>
                  <div style={{ fontSize:'11px', color:MUTED, marginTop:'1px' }}>{opt.sub}</div>
                </div>
              </button>
            ))}
          </div>
          {advanceType === 'percent' && (
            <div style={{ marginTop:'12px' }}>
              <FieldInput label="Advance percentage (%)" value={advanceValue} onChange={setAdvanceValue} type="number" placeholder="e.g. 30"
                hint={form.sell_price && advanceValue ? 'Approx ' + sym + Math.round(Number(form.sell_price) * Number(advanceValue) / 100) + ' on a ' + sym + form.sell_price + ' order' : ''} />
            </div>
          )}
          {advanceType === 'fixed' && (
            <div style={{ marginTop:'12px' }}>
              <FieldInput label={'Advance amount (' + sym + ')'} value={advanceValue} onChange={setAdvanceValue} type="number" placeholder="e.g. 200" />
            </div>
          )}
          {advanceType === 'full' && (
            <div style={{ marginTop:'12px', padding:'10px 13px', borderRadius:'9px', background:'rgba(13,148,136,0.07)', border:'1px solid rgba(13,148,136,0.25)', fontSize:'11.5px', color:TEAL, lineHeight:1.6 }}>
              Customer must pay the full amount before the order is confirmed. No COD for this product.
            </div>
          )}
        </SectionCard>

        {/* Weight */}
        <SectionCard title={`Step ${5+stepOffset} — Weight`} color={MUTED} hint={'Used for courier bookings. Enter in ' + weightUnit + '. e.g. 0.250 = 250g'}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }} className="ap-grid">
            <div>
              <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'5px' }}>Product weight ({weightUnit}) <span style={{ fontWeight:400, color:'#B0B3B8' }}>optional</span></label>
              <div style={{ position:'relative' }}>
                <input type="number" step="0.001" min="0" value={form.weight} onChange={e=>set('weight',e.target.value)}
                  placeholder={weightUnit==='kg'?'e.g. 0.250 for 250g':'e.g. 0.5'} style={{ ...inp, borderColor:BORDER, paddingRight:'40px' }}/>
                <span style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', fontSize:'11.5px', fontWeight:700, color:MUTED }}>{weightUnit}</span>
              </div>
              {form.weight&&<div style={{ fontSize:'11.5px', color:'#16A34A', marginTop:'4px', fontWeight:600 }}>{formatWeight(form.weight,weightUnit)}</div>}
            </div>
            {(form.stock||variants.length>0)&&form.weight&&(
              <div style={{ padding:'12px 14px', borderRadius:'10px', background:'#EFF6FF', border:'1px solid #BFDBFE' }}>
                <div style={{ fontSize:'11px', fontWeight:700, color:BLUE, marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Weight summary</div>
                {totalWeight()&&<div style={{ fontSize:'13px', fontWeight:700, color:BLUE }}>Total: {totalWeight()}</div>}
              </div>
            )}
          </div>
        </SectionCard>

        {/* Stock & variants */}
        <SectionCard title={`Step ${6+stepOffset} — Stock and variants`} color={PURPLE} hint="Variants are options you cannot see in a photo — size, weight, pack size, flavour.">
          <div style={{ display:'flex', gap:'6px', marginBottom:'16px', padding:'4px', borderRadius:'10px', background:BG, width:'fit-content' }}>
            {[{key:'simple',label:'Simple add'},{key:'matrix',label:'Matrix builder'}].map(m=>(
              <button key={m.key}
                onClick={()=>{ setVariantMode(m.key); if(m.key==='simple'){ const s=VARIANT_SUGGESTIONS[form.category]; if(s&&variants.length===0){ setVariants(s.flatMap(dim=>dim.values.split(',').map(v=>({variant_name:v.trim(),stock:'',sell_price:'',weight:'',variant_code:generateVariantCode(productCode,v.trim())})))) } else { setVariants([]) } } else { setVariants([]) } }}
                style={{ padding:'6px 14px', borderRadius:'7px', border:'none', fontSize:'12px', fontWeight:600, cursor:'pointer', background:variantMode===m.key?PURPLE:'transparent', color:variantMode===m.key?WHITE:MUTED }}>
                {m.label}
              </button>
            ))}
          </div>
          {variantMode==='simple'&&(
            <>
              {variants.length===0&&(
                <FieldInput label="Stock" value={form.stock} onChange={v=>set('stock',v)} placeholder="0" type="number" hint="Only fill this if the product has NO variants."/>
              )}
              {variants.length>0&&(
                <div style={{ marginBottom:'14px' }}>
                  <div style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, marginBottom:'8px' }}>Stock per variant</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'7px' }}>
                    {variants.map((v,i)=>(
                      <div key={i} style={{ display:'flex', flexDirection:'column', gap:'5px', padding:'10px', borderRadius:'10px', border:'1px solid #DDD6FE', background:'#FAFAFF' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                          <div style={{ padding:'5px 10px', borderRadius:'7px', background:'#F5F3FF', border:'1px solid #DDD6FE', fontSize:'12px', fontWeight:700, color:PURPLE, flexShrink:0 }}>{v.variant_name}</div>
                          <input type="number" value={v.stock} onChange={e=>handleVariantChange(i,'stock',e.target.value)} placeholder="Stock" style={{ ...inp, flex:1, borderColor:BORDER }}/>
                          <input type="number" value={v.sell_price} onChange={e=>handleVariantChange(i,'sell_price',e.target.value)} placeholder="Different price?" style={{ ...inp, flex:1.5, borderColor:BORDER }}/>
                          <div style={{ position:'relative', flex:1 }}>
                            <input type="number" step="0.001" value={v.weight} onChange={e=>handleVariantChange(i,'weight',e.target.value)} placeholder={form.weight||'Weight'} style={{ ...inp, borderColor:BORDER, paddingRight:'32px' }}/>
                            <span style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', fontSize:'10px', color:MUTED, fontWeight:700 }}>{weightUnit}</span>
                          </div>
                          <button onClick={()=>setVariants(vs=>vs.filter((_,idx)=>idx!==i))} style={{ width:'28px', height:'28px', borderRadius:'7px', background:'#FEF2F2', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
                          </button>
                        </div>
                        {v.variant_code&&<div style={{ fontSize:'10.5px', fontFamily:'JetBrains Mono, monospace', color:PURPLE, fontWeight:600 }}>{v.variant_code}</div>}
                      </div>
                    ))}
                    <div style={{ fontSize:'11.5px', color:MUTED, marginTop:'4px' }}>
                      Total stock: <strong style={{ color:TEXT }}>{variants.reduce((s,v)=>s+Number(v.stock||0),0)}</strong>
                    </div>
                  </div>
                </div>
              )}
              <div style={{ borderTop:'1px solid '+BORDER, paddingTop:'12px', marginTop:'12px' }}>
                <div style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, marginBottom:'4px' }}>Add a variant</div>
                <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                  <input type="text" value={newVariant.variant_name} onChange={e=>setNewVariant(v=>({...v,variant_name:e.target.value}))}
                    onKeyDown={e=>e.key==='Enter'&&addVariant()} placeholder="e.g. XL, Red, 500g, Chocolate" style={{ ...inp, flex:2, borderColor:BORDER, minWidth:'120px' }}/>
                  <input type="number" value={newVariant.stock} onChange={e=>setNewVariant(v=>({...v,stock:e.target.value}))} placeholder="Qty" style={{ ...inp, width:'70px', borderColor:BORDER }}/>
                  <div style={{ position:'relative', width:'90px' }}>
                    <input type="number" step="0.001" value={newVariant.weight} onChange={e=>setNewVariant(v=>({...v,weight:e.target.value}))} placeholder="Wt" style={{ ...inp, width:'100%', borderColor:BORDER, paddingRight:'28px' }}/>
                    <span style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', fontSize:'9px', color:MUTED, fontWeight:700 }}>{weightUnit}</span>
                  </div>
                  <button onClick={addVariant} style={{ padding:'9px 16px', borderRadius:'9px', background:PURPLE, border:'none', fontSize:'12.5px', fontWeight:600, color:WHITE, cursor:'pointer', flexShrink:0 }}>Add</button>
                </div>
              </div>
            </>
          )}
          {variantMode==='matrix'&&(
            <MatrixBuilder variants={variants} setVariants={setVariants} generateVariantCode={generateVariantCode}
              productCode={productCode} defaultPrice={form.sell_price} suggestedDimensions={suggestedDimensions} weightUnit={weightUnit}/>
          )}
        </SectionCard>

        {/* Offer */}
        <SectionCard title={`Step ${7+stepOffset} — Offer / discount`} color={GREEN}>
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:BG, borderRadius:'10px' }}>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:TEXT }}>Offer active</div>
                <div style={{ fontSize:'11.5px', color:MUTED, marginTop:'2px' }}>Toggle on to attach a discount to this product</div>
              </div>
              <button onClick={()=>setOfferActive(v=>!v)} style={{ width:'40px', height:'22px', borderRadius:'11px', background:offerActive?GREEN:BORDER, border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                <div style={{ position:'absolute', top:'3px', left:offerActive?'21px':'3px', width:'16px', height:'16px', borderRadius:'50%', background:WHITE, transition:'left 0.2s' }}/>
              </button>
            </div>
            {offerActive && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }} className="ap-grid">
                <div>
                  <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'6px' }}>Offer type</label>
                  <div style={{ display:'flex', gap:'6px' }}>
                    {[{v:'percent',l:'% Off'},{v:'fixed',l:'Fixed off'},{v:'label',l:'Label only'}].map(opt=>(
                      <button key={opt.v} onClick={()=>setOfferType(opt.v)}
                        style={{ flex:1, padding:'8px 6px', borderRadius:'8px', border:'1px solid', borderColor:offerType===opt.v?GREEN:BORDER, background:offerType===opt.v?'#F0FDF4':WHITE, fontSize:'11.5px', fontWeight:600, color:offerType===opt.v?GREEN:MUTED, cursor:'pointer' }}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
                {offerType !== 'label' && (
                  <FieldInput label={offerType==='percent'?'Discount %':'Discount amount ('+sym+')'}
                    value={offerValue} onChange={setOfferValue}
                    placeholder={offerType==='percent'?'e.g. 15':'e.g. 200'} type="number"/>
                )}
                <div style={{ gridColumn:'span 2' }}>
                  <FieldInput label="Offer label" value={offerLabel} onChange={setOfferLabel}
                    placeholder="e.g. Buy 2 Get 1 Free, Eid Special, Flash Sale" optional/>
                </div>
                <div>
                  <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'5px' }}>Offer starts <span style={{ fontWeight:400, color:'#B0B3B8' }}>optional</span></label>
                  <input type="date" value={offerValidFrom} onChange={e=>setOfferValidFrom(e.target.value)} style={{ ...inp, borderColor:BORDER, cursor:'pointer' }}/>
                </div>
                <div>
                  <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'5px' }}>Offer ends <span style={{ fontWeight:400, color:'#B0B3B8' }}>optional</span></label>
                  <input type="date" value={offerValidUntil} onChange={e=>setOfferValidUntil(e.target.value)} style={{ ...inp, borderColor:BORDER, cursor:'pointer' }}/>
                </div>
              </div>
            )}
          </div>
        </SectionCard>

        {/* Other details */}
        <SectionCard title="Other details" color={MUTED}>
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }} className="ap-grid">
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:MUTED, display:'block', marginBottom:'5px' }}>
                  Supplier barcode / EAN <span style={{ fontWeight:400, color:'#B0B3B8' }}>optional</span>
                </label>
                <div style={{ display:'flex', gap:'8px' }}>
                  <input value={form.external_barcode} onChange={e=>set('external_barcode',e.target.value.replace(/\s/g,''))}
                    placeholder="e.g. 8901234567890 — or scan below"
                    style={{ ...inp, fontFamily:'JetBrains Mono, monospace', borderColor:BORDER, flex:1 }}/>
                  <button type="button" onClick={barcodeCamera?stopBarcodeCamera:openBarcodeCamera}
                    style={{ padding:'9px 14px', borderRadius:'9px', background:barcodeCamera?'#DC2626':TEXT, border:'none', fontSize:'12.5px', fontWeight:600, color:WHITE, cursor:'pointer', flexShrink:0 }}>
                    {barcodeCamera?'Stop':'Scan'}
                  </button>
                </div>
                {barcodeCamera&&(
                  <div style={{ marginTop:'8px', borderRadius:'10px', overflow:'hidden', background:TEXT, position:'relative' }}>
                    <video ref={barcodeVideoRef} style={{ width:'100%', maxHeight:'180px', objectFit:'cover', display:'block' }} playsInline muted/>
                    {barcodeScanErr&&<div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'8px 12px', background:'rgba(0,0,0,0.75)', color:WHITE, fontSize:'11.5px' }}>{barcodeScanErr}</div>}
                  </div>
                )}
                {form.external_barcode&&<div style={{ marginTop:'5px', padding:'5px 10px', borderRadius:'7px', background:'#F0FDF4', border:'1px solid #BBF7D0', fontSize:'12px', color:'#16A34A', fontFamily:'JetBrains Mono, monospace', fontWeight:600 }}>✓ {form.external_barcode}</div>}
              </div>
              <FieldInput label="Facebook post URL" value={form.facebook_post_url} onChange={v=>set('facebook_post_url',v)} placeholder="https://facebook.com/..." optional/>
            </div>
            <FieldTextarea label="Internal notes" value={form.notes} onChange={v=>set('notes',v)} placeholder="Private notes — not shown to customers..." optional/>
          </div>
        </SectionCard>

        {/* Save */}
        <div style={{ marginBottom:'40px' }}>
          {duplicateWarning&&<div style={{ padding:'10px 14px', borderRadius:'9px', background:'#FEF2F2', border:'1px solid #FECACA', color:'#DC2626', fontSize:'12px', marginBottom:'10px' }}>Product code already exists — change the code before saving.</div>}
          {!form.category&&<div style={{ padding:'10px 14px', borderRadius:'9px', background:'#FFF8E6', border:'1px solid #FDE68A', fontSize:'12px', color:'#92400E', marginBottom:'10px' }}>Select a category first.</div>}
          <button onClick={handleSave}
            disabled={saving||uploading||!form.name||!form.category||!productCode||duplicateWarning}
            style={{ width:'100%', padding:'13px', borderRadius:'10px', background:BLUE, border:'none', fontSize:'14px', fontWeight:700, color:WHITE, cursor:'pointer', opacity:(saving||uploading||!form.name||!form.category||!productCode||duplicateWarning)?0.6:1 }}>
            {saving?'Saving...':uploading?'Uploading photo...':'Save product'}
          </button>
        </div>
      </div>

      {cropSrc&&<CropModal src={cropSrc} onSave={handleCropSave} onCancel={()=>setCropSrc(null)}/>}

      <style>{`
        @media(max-width:640px){
          .ap-grid{grid-template-columns:1fr !important}
          .ap-grid-3{grid-template-columns:1fr 1fr !important}
          .ap-grid > *,.ap-grid-3 > *{grid-column:span 1 !important}
        }
      `}</style>
    </div>
  )
}

export default AddProduct